import { useLayoutEffect, useMemo, useRef, type CSSProperties, type PointerEventHandler, type ReactNode } from 'react'
import { AnimatePresence, motion, type Transition } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { useIsDark } from '@highlight/ui'
import { FadingScrollable } from '@renderer/chat'
import { Skeleton } from '@renderer/components/ui/skeleton'
import {
  useClaimSidepanelBottomGlow,
  useIsViewSheetOpen,
  useSidepanelBottomGlowTint,
  useSidepanelFloatingBarHeight,
} from '../../atoms'
import { useViewEntryScrollPersistence } from '../../hooks/use-view-scroll-persistence'
import { useSidepanelSurfaceBackground } from '../../hooks/use-sidepanel-surface-background'
import {
  useViewBodyBottomChrome,
  useViewBodyBottomInset,
  VIEW_BOTTOM_FADE_SPAN_PX,
} from '../../hooks/use-view-body-bottom-inset'
import { SIDEPANEL_LAYER_Z } from '../../constants'

export { useViewBodyBottomChrome, useViewBodyBottomInset, VIEW_BOTTOM_FADE_SPAN_PX }

interface ViewProps {
  children: ReactNode
  className?: string
  /**
   * Optional color for the bottom glow (e.g. the Action View's state colors).
   * Painted by the host layout's `View.Glow`, so the wash originates behind
   * the composer rather than inside the view's own box.
   */
  bottomFadeTint?: string
}

/**
 * The visual surface a View's content sits on: panel translucency and the
 * standing bottom fade. Geometry and motion belong to the view router — this
 * fills whatever box the router positions it in.
 */
function View({ children, className, bottomFadeTint }: ViewProps) {
  const surfaceBackground = useSidepanelSurfaceBackground()
  useClaimSidepanelBottomGlow(bottomFadeTint)

  return (
    // `clip` avoids turning the surface into a second scroll container when a
    // descendant calls scrollIntoView.
    // In the overlay the view sits inside the panel card, so its bottom corners
    // take the card's `rounded-2xl`. A popout has
    // no card; the window is the surface.
    <div
      className={twMerge(
        'overlay:rounded-b-2xl overlay:corner-smooth flex h-full w-full flex-col overflow-clip',
        surfaceBackground,
        className,
      )}
    >
      {children}
    </div>
  )
}

interface ViewHeaderProps {
  icon: ReactNode
  title: string
  /** Right-aligned control on the title row (a filter menu, a count). */
  accessory?: ReactNode
  /** Rows beneath the title: search pill, tabs, inline inputs. */
  children?: ReactNode
}

/**
 * The fixed block above a view's scrolling body: title lockup plus any controls.
 *
 * The title row carries its own `px-3.5` on top of the block's `px-3`: per the
 * designs the lockup sits 26px in, indented past the search pill and the rows
 * below it, exactly as Home's section headers are (Figma `7095:11743`).
 */
function ViewHeader({ icon, title, accessory, children }: ViewHeaderProps) {
  return (
    <div className="flex shrink-0 flex-col gap-3 px-3 pb-3 pt-3">
      <div className="flex items-center justify-between gap-2 px-3.5 py-1.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="text-foreground-secondary shrink-0 [&>svg]:size-3.5" aria-hidden="true">
            {icon}
          </span>
          <h2 className="text-foreground type-ui-sm truncate font-medium leading-none">{title}</h2>
        </div>
        {accessory}
      </div>
      {children}
    </div>
  )
}

interface ViewBodyProps {
  children: ReactNode
  className?: string
  /**
   * How much room to leave under the last of the content.
   *
   * `fade` clears the bottom dissolve entirely — for prose and detail bodies,
   * where a sentence washing out mid-word reads as a rendering bug. `list`
   * leaves a row's worth of breathing room and lets the final rows pass under
   * the dissolve, which is the intended treatment for scrolling lists.
   */
  bottomInset?: 'fade' | 'list'
  /**
   * Receives the scroll element once mounted, for virtualizers that need it as
   * a value rather than a ref (Virtuoso's `customScrollParent`, TanStack's
   * `getScrollElement`). Pass a stable callback.
   */
  onScrollElement?: (element: HTMLDivElement | null) => void
  /** Called for the entire scroll body, including its bottom padding. */
  onPointerDown?: PointerEventHandler<HTMLDivElement>
}

/**
 * A view's scrolling body, and the single owner of the three things every view
 * body needs: the scroll container, per-entry scroll persistence, and enough
 * bottom padding to clear the floating toolbar and its fade.
 *
 * These were hand-rolled per view once, and the padding drifted across five
 * different values — so the fade contract was documented in one place and
 * honoured in none. Pick an inset here instead.
 */
function ViewBody({ children, className, bottomInset = 'fade', onScrollElement, onPointerDown }: ViewBodyProps) {
  const paddingBottom = useViewBodyBottomInset(bottomInset)
  const bottomChrome = useViewBodyBottomChrome()
  const scrollRef = useRef<HTMLDivElement | null | undefined>(null)
  useViewEntryScrollPersistence(scrollRef)

  useLayoutEffect(() => {
    onScrollElement?.(scrollRef.current ?? null)
    return () => onScrollElement?.(null)
  }, [onScrollElement])

  return (
    <FadingScrollable
      ref={scrollRef}
      className={twMerge('min-h-0 flex-1 overflow-y-auto overscroll-contain px-3', className)}
      direction="vertical"
      fadeSize="24px"
      bottomChrome={bottomChrome}
      style={{ paddingBottom }}
      onPointerDown={onPointerDown}
    >
      {children}
    </FadingScrollable>
  )
}

interface ViewEmptyProps {
  /** When set, the message becomes "no results" for this query instead. */
  searchQuery?: string
  /**
   * A second, dimmer line under the no-results message — for views whose search
   * covers less than the user might assume (History searches loaded pages only).
   * Ignored when there's no query.
   */
  searchHint?: ReactNode
  /** The resting empty message — what this view says when it simply has nothing. */
  children: ReactNode
}

interface ViewGlowProps {
  className?: string
  fadeAtInlineEdges?: boolean
}

/**
 * Widest the wash gets. On a wide popout the element still spans the window,
 * but the gradient is painted at this width and centered, so it stays a glow
 * behind the composer rather than a band across the whole bottom edge.
 */
const VIEW_GLOW_MAX_WIDTH_PX = 960

const VIEW_GLOW_HIDDEN = { opacity: 0 }
const VIEW_GLOW_TRANSITION: Transition = { duration: 0.2, ease: 'easeOut' }

/**
 * The bottom-center wash a View asked for with `bottomFadeTint`. Hosts render
 * it inside their footer, the positioned box the composer sits in. It fills
 * that box and reaches up past the floating bar and the body's fade span, so
 * the glow starts behind the chat input and rises over the open View.
 *
 * It carries its own z-index because the router composites with
 * `will-change: transform` and would hide a same-z sibling.
 *
 * Hosts keep this mounted; the wash itself fades in and out as a view claims
 * or releases a tint, and while a bottom sheet is open, since the footer wash
 * would otherwise paint through the sheet.
 */
function ViewGlow({ className, fadeAtInlineEdges = false }: ViewGlowProps) {
  const glowTint = useSidepanelBottomGlowTint()
  const floatingBarHeight = useSidepanelFloatingBarHeight()
  const isViewSheetOpen = useIsViewSheetOpen()
  const isDark = useIsDark()
  const tint = isViewSheetOpen ? null : glowTint

  const style: CSSProperties | undefined = useMemo(() => {
    if (!tint) {
      return undefined
    }
    return {
      height: `calc(100% + ${floatingBarHeight + VIEW_BOTTOM_FADE_SPAN_PX}px)`,
      // CSS percentages describe the ellipse's radius, not its diameter.
      // A 68% radius reaches the transparent stop just inside each side.
      backgroundImage: `radial-gradient(${fadeAtInlineEdges ? '70%' : '138%'} 110% at 50% 100%, ${tint}, transparent 72%)`,
      backgroundSize: `min(100%, ${VIEW_GLOW_MAX_WIDTH_PX}px) 100%`,
      backgroundPosition: 'center bottom',
      backgroundRepeat: 'no-repeat',
      zIndex: SIDEPANEL_LAYER_Z.bottomGlow,
    }
  }, [floatingBarHeight, fadeAtInlineEdges, tint])

  return (
    <AnimatePresence>
      {tint && (
        <motion.div
          key="glow"
          aria-hidden="true"
          className={twMerge('pointer-events-none absolute inset-x-0 bottom-0', className)}
          initial={VIEW_GLOW_HIDDEN}
          // Dark surfaces need half the wash to read the same.
          animate={{ opacity: isDark ? 0.5 : 1 }}
          exit={VIEW_GLOW_HIDDEN}
          transition={VIEW_GLOW_TRANSITION}
          style={style}
        />
      )}
    </AnimatePresence>
  )
}

interface ViewToolbarProps {
  children: ReactNode
  className?: string
}

/** Layout boundary for controls a view contributes to the shared floating bar. */
function ViewToolbar({ children, className }: ViewToolbarProps) {
  return <div className={twMerge('flex min-w-0 items-center gap-2', className)}>{children}</div>
}

/** The centered empty message a list view shows in place of its rows. */
function ViewEmpty({ searchQuery, searchHint, children }: ViewEmptyProps) {
  return (
    <div className="text-foreground-secondary type-ui-sm flex min-h-[200px] flex-col items-center justify-center">
      {searchQuery ? (
        <>
          {`No results for “${searchQuery}”`}
          {searchHint && (
            <span className="text-foreground-secondary type-ui-xs mt-1 px-6 text-center">{searchHint}</span>
          )}
        </>
      ) : (
        children
      )}
    </div>
  )
}

/** Stand-in for a view whose content hasn't loaded: a title, a subtitle, a body. */
function ViewSkeleton({ className }: { className?: string }) {
  return (
    <div className={twMerge('flex w-full flex-col gap-4 p-4', className)}>
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}

View.Header = ViewHeader
View.Body = ViewBody
View.Toolbar = ViewToolbar
View.Empty = ViewEmpty
View.Glow = ViewGlow
View.Skeleton = ViewSkeleton

export { View }
